import { useEffect, useMemo, useState } from "react";

import * as StateLib from "@/Utils/StateLib";
import FieldToggleMenu from "../../../../Menus/FieldToggleMenu/FieldToggleMenu";
import SectionCard, { SectionCardGroup } from "../../../../Composites/SectionCard/SectionCard";
import { addOrRemove } from "@/Utils/Misc";
import { Button, Flex, isNullish, List, Pagination } from "@theorgchartdev/storybook";
import { getLocalizedPaginationStrings } from "@/Utils/Utils";
import { appstr } from "@coreapp/utils/appstr";
import {
  ChartSettingsSectionRoutes,
  getChartSettingsTestPrefix
} from "@/providers/NavigationProvider";

const SEARCH_DIRECTORY_ROUTE = ChartSettingsSectionRoutes.SearchAndDirectory;

export const SEARCH_DIRECTORY_TEST_PREFIX = getChartSettingsTestPrefix(SEARCH_DIRECTORY_ROUTE);

const BuildListOptions = keys => {
  let options = [];

  for (let key of keys) {
    let keyOption = {
      label: key,
      divider: true,
      value: key
    };
    options.push(keyOption);
  }

  return options;
};

export const SearchDirectoryConfiguration = ({ chartConfig, handleUpdate }) => {
  const [dirtySearchFields, setDirtySearchFields] = useState(false);
  const [dirtyResultFields, setDirtyResultFields] = useState(false);
  const [advPaginatedIndices, setAdvPaginatedIndices] = useState([0, 0]);
  const [dirPaginatedIndices, setDirPaginatedIndices] = useState([0, 0]);

  const advSearchKeys = useMemo(() => {
    if (!dirtySearchFields && isNullish(chartConfig?.keys)) {
      return chartConfig?.headers;
    }

    return chartConfig.keys.map(key => key.keyDef);
  }, [dirtySearchFields, JSON.stringify(chartConfig?.keys)]);

  const directoryFields = useMemo(() => {
    if (!dirtyResultFields && isNullish(chartConfig?.directory?.fields)) {
      return chartConfig?.headers;
    }

    return chartConfig.directory.fields;
  }, [dirtyResultFields, JSON.stringify(chartConfig?.directory)]);

  if (!chartConfig) return;

  const wsMgr = StateLib.GetWSMgr();
  const eventTracker = wsMgr.GetUsageTracker();

  useEffect(() => {
    eventTracker.ReportEvent("chart_settings.search_and_directory.opened");
  }, []);

  const getKeyName = keyId => {
    const fieldKeys = Object.keys(chartConfig.fields);
    return fieldKeys.find(key => chartConfig.fields[key] === keyId);
  };

  const ToggleField = (target, listID) => {
    const updatedChartConfig = structuredClone(chartConfig);
    let syncKey = "";
    switch (listID) {
      case "advancedSearchKeys":
        const nextSearchKeys = addOrRemove(advSearchKeys, target.value).sort();
        updatedChartConfig.keys = nextSearchKeys.map(key => ({
          keyDef: key,
          keyName: getKeyName(key),
          isLookup: true
        }));
        setDirtySearchFields(true);
        syncKey = "keys";
        break;
      case "directoryReturnFields":
        updatedChartConfig.directory.fields = addOrRemove(directoryFields, target.value).sort();
        setDirtyResultFields(true);
        syncKey = "directory";
        break;
      default:
        break;
    }

    handleUpdate(updatedChartConfig, syncKey);
  };

  const handleSetSelectAll = listID => shouldSelectAll => {
    const updatedChartConfig = structuredClone(chartConfig);
    let syncKey = "";

    switch (listID) {
      case "advancedSearchKeys":
        syncKey = "keys";
        setDirtySearchFields(true);

        if (shouldSelectAll) {
          updatedChartConfig.keys = [...chartConfig.headers].sort().map(field => ({
            keyDef: field,
            keyName: getKeyName(field),
            isLookup: true
          }));
        } else {
          updatedChartConfig.keys = [];
        }
        break;
      case "directoryReturnFields":
        syncKey = "directory";
        setDirtyResultFields(true);

        if (shouldSelectAll) {
          updatedChartConfig.directory.fields = [...chartConfig.headers].sort();
        } else {
          updatedChartConfig.directory.fields = [];
        }
        break;
    }

    handleUpdate(updatedChartConfig, syncKey);
  };

  const makeFieldTrigger = dataTestId => (
    <Button
      modifier="secondary"
      type="button"
      className={"oc-special-secondary"}
      dataTestId={dataTestId}
    >
      {appstr("Choose fields")}
    </Button>
  );

  const advancedSearchOptions = BuildListOptions(advSearchKeys);
  const dirReturnOptions = BuildListOptions(directoryFields);
  const headerFields = chartConfig?.headers ?? [];

  return (
    <SectionCardGroup>
      <SectionCard
        title={appstr("Advanced search")}
        titleContent={
          <FieldToggleMenu
            dataTestId={`${SEARCH_DIRECTORY_ROUTE}-search-ManageFields`}
            dataTestIdPrefix={`${SEARCH_DIRECTORY_ROUTE}-search-`}
            listProps={{
              listHeader: {
                label: (
                  <SelectAllControl
                    dataTestIdPrefix={`${SEARCH_DIRECTORY_ROUTE}-search-`}
                    areAllSelected={advSearchKeys.length === chartConfig?.headers?.length}
                    onClick={handleSetSelectAll("advancedSearchKeys")}
                  />
                )
              }
            }}
            selectedFieldNames={advSearchKeys}
            fieldNames={headerFields}
            onChange={target => ToggleField(target, "advancedSearchKeys")}
            trigger={makeFieldTrigger(`${SEARCH_DIRECTORY_TEST_PREFIX}-search-choose-fields-btn`)}
            position="bottom-end"
          />
        }
      >
        <List
          enableVirtualization={false}
          options={advancedSearchOptions.slice(advPaginatedIndices[0], advPaginatedIndices[1])}
          listWidth="fill"
          optionSize={"large"}
          showCheck={false}
        />
        <Pagination
          modifier="full"
          data={advSearchKeys}
          onDataChange={setAdvPaginatedIndices}
          textTags={getLocalizedPaginationStrings()}
          preserveSelectedPage
          dataTestIdPrefix={`${SEARCH_DIRECTORY_TEST_PREFIX}-search-pagination-`}
        />
      </SectionCard>
      <SectionCard
        title={appstr("Directory result fields")}
        titleContent={
          <FieldToggleMenu
            dataTestId={`${SEARCH_DIRECTORY_ROUTE}-result-ManageFields`}
            dataTestIdPrefix={`${SEARCH_DIRECTORY_ROUTE}-result-`}
            listProps={{
              listHeader: {
                label: (
                  <SelectAllControl
                    dataTestIdPrefix={`${SEARCH_DIRECTORY_ROUTE}-result-`}
                    areAllSelected={directoryFields.length === chartConfig?.headers?.length}
                    onClick={handleSetSelectAll("directoryReturnFields")}
                  />
                )
              }
            }}
            selectedFieldNames={directoryFields}
            fieldNames={headerFields}
            onChange={target => ToggleField(target, "directoryReturnFields")}
            trigger={makeFieldTrigger(`${SEARCH_DIRECTORY_TEST_PREFIX}-result-choose-fields-btn`)}
            position="bottom-end"
          />
        }
      >
        <List
          enableVirtualization={false}
          options={dirReturnOptions.slice(dirPaginatedIndices[0], dirPaginatedIndices[1])}
          listWidth="fill"
          optionSize={"large"}
          showCheck={false}
        />
        <Pagination
          modifier="full"
          data={directoryFields}
          onDataChange={setDirPaginatedIndices}
          textTags={getLocalizedPaginationStrings()}
          preserveSelectedPage
          dataTestIdPrefix={`${SEARCH_DIRECTORY_TEST_PREFIX}-result-pagination-`}
        />
      </SectionCard>
    </SectionCardGroup>
  );
};

export const SelectAllControl = props => (
  <Flex width="100%" direction="row" justifyContent="flex-end">
    <Button
      dataTestId={`${props.dataTestIdPrefix}toggleSelectAll`}
      size={props.size ?? "small"}
      modifier={"secondary"}
      onClick={() => props.onClick(!props.areAllSelected)}
    >
      {props.areAllSelected ? appstr("Deselect All") : appstr("Select All")}
    </Button>
  </Flex>
);
